import * as React from 'react';
import { isUserEnrolled } from '@/repos/enrollmentRepo';
import { getAccessibleModuleIds } from '@/repos/moduleEnrollmentRepo';

export interface CourseAccessState {
  loading: boolean;
  isFullCourseEnrolled: boolean;
  accessibleModuleIds: string[];
  hasPartialModuleAccess: boolean;
  hasAnyAccess: boolean;
  isModuleAccessible: (moduleId: string | undefined) => boolean;
  refresh: () => void;
}

/**
 * Escopo de acesso do aluno a um curso (matrícula completa ou módulos parciais).
 * @param systemUserId — users.id (enrollments / module_enrollments)
 */
export function useCourseAccess(
  systemUserId?: string,
  courseId?: string
): CourseAccessState {
  const [loading, setLoading] = React.useState(false);
  const [isFullCourseEnrolled, setIsFullCourseEnrolled] = React.useState(false);
  const [accessibleModuleIds, setAccessibleModuleIds] = React.useState<string[]>([]);

  const fetchAccess = React.useCallback(async () => {
    if (!systemUserId || !courseId) {
      setIsFullCourseEnrolled(false);
      setAccessibleModuleIds([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [fullEnrolled, moduleIds] = await Promise.all([
        isUserEnrolled(systemUserId, courseId),
        getAccessibleModuleIds(systemUserId, courseId),
      ]);
      setIsFullCourseEnrolled(fullEnrolled);
      setAccessibleModuleIds(moduleIds);
    } catch (error) {
      console.error('❌ useCourseAccess:', error);
      setIsFullCourseEnrolled(false);
      setAccessibleModuleIds([]);
    } finally {
      setLoading(false);
    }
  }, [systemUserId, courseId]);

  React.useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  const isModuleAccessible = React.useCallback(
    (moduleId: string | undefined) => {
      if (!moduleId) return false;
      if (isFullCourseEnrolled) return true;
      return accessibleModuleIds.includes(String(moduleId));
    },
    [isFullCourseEnrolled, accessibleModuleIds]
  );

  const hasPartialModuleAccess =
    !isFullCourseEnrolled && accessibleModuleIds.length > 0;
  const hasAnyAccess = isFullCourseEnrolled || accessibleModuleIds.length > 0;

  return {
    loading,
    isFullCourseEnrolled,
    accessibleModuleIds,
    hasPartialModuleAccess,
    hasAnyAccess,
    isModuleAccessible,
    refresh: fetchAccess,
  };
}
